import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { DEFAULT_COLORS } from './DefaultMaterial';

// Shared geometry - created once, reused for all blocks
const BLOCK_SIZE = 0.88;
const BLOCK_DEPTH = BLOCK_SIZE * 0.75;
const sharedBlockGeometry = new THREE.BoxGeometry(BLOCK_SIZE, BLOCK_SIZE, BLOCK_DEPTH);
const sharedEdgeGeometry = new THREE.EdgesGeometry(new THREE.BoxGeometry(BLOCK_SIZE * 1.02, BLOCK_SIZE * 1.02, BLOCK_DEPTH * 0.82));

// Single color for all blocks - simple purple
const SINGLE_BLOCK_COLOR = '#9B59B6';

const DefaultBlock3D = ({
  position,
  colorClass,
  isGhost = false,
  isActive = false,
  animate = true,
  scale = 1
}) => {
  const groupRef = useRef();
  const edgeRef = useRef();

  // Simple pulse animation for active pieces only
  useFrame((state) => {
    const time = state.clock.elapsedTime;

    // Very subtle scale pulse for active pieces
    if (groupRef.current && animate && isActive) {
      const pulse = Math.sin(time * 3) * 0.015 + 1;
      groupRef.current.scale.setScalar(pulse);
    }

    // Subtle edge opacity pulse
    if (edgeRef.current?.material && !isGhost) {
      const edgePulse = isActive
        ? 0.6 + Math.sin(time * 3) * 0.2
        : 0.4 + Math.sin(time * 2) * 0.1;
      edgeRef.current.material.opacity = edgePulse;
    }
  });

  // Main material - simple matte look, no glow
  const material = useMemo(() => {
    if (isGhost) {
      return new THREE.MeshStandardMaterial({
        color: new THREE.Color(SINGLE_BLOCK_COLOR),
        emissive: new THREE.Color('#000000'),
        emissiveIntensity: 0,
        metalness: 0,
        roughness: 1,
        opacity: 0.25,
        transparent: true,
        side: THREE.DoubleSide,
      });
    }

    return new THREE.MeshStandardMaterial({
      color: new THREE.Color(SINGLE_BLOCK_COLOR),
      emissive: new THREE.Color('#000000'),
      emissiveIntensity: 0,
      metalness: 0.1,
      roughness: 0.8,
      side: THREE.DoubleSide,
    });
  }, [isGhost]);

  const blockScale = scale;

  return (
    <group position={position} ref={groupRef} scale={blockScale}>
      {/* Main block body - using shared geometry */}
      <mesh
        geometry={sharedBlockGeometry}
        material={material}
        castShadow={!isGhost}
        receiveShadow={!isGhost}
      />

      {/* Simple edge outline - no glow */}
      {!isGhost && (
        <lineSegments ref={edgeRef} geometry={sharedEdgeGeometry}>
          <lineBasicMaterial
            color={SINGLE_BLOCK_COLOR}
            linewidth={1}
            opacity={0.5}
            transparent
          />
        </lineSegments>
      )}
    </group>
  );
};

export default DefaultBlock3D;
